// =============================================================================
// LedgerPass — App.tsx
// =============================================================================
// Single-page React application implementing the Path A stateless payment
// lifecycle from the Technical Specification.
//
// State Machine:
//   IDLE → UPLOADING → PREVIEW → PAYING → PROCESSING → COMPLETE
//
// Key invariant: The raw File object is held in React state (`csvFile`)
// across the entire Stripe Elements payment overlay. It is never released
// until the download completes or the user explicitly removes it.
// =============================================================================

import React, {
  useState,
  useRef,
  useCallback,
  useMemo,
  useEffect,
  type DragEvent,
  type ChangeEvent,
} from 'react';
// The '/pure' entry point disables @stripe/stripe-js's import-time side
// effect of injecting the Stripe.js <script> — without it, Stripe.js loads
// on first paint for every visitor even if loadStripe() is never called.
import { loadStripe } from '@stripe/stripe-js/pure';
import { type StripeElementsOptions } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import './index.css';
import BrandLogo from './components/BrandLogo';
import { seoSolutions, type SeoSolution } from './engine/seoSolutions';

// =============================================================================
// §1  Configuration
// =============================================================================

const CONFIG = {
  /** Cloudflare Worker API base URL. */
  API_BASE: import.meta.env.VITE_API_BASE ?? 'https://api.getledgerpass.com',
  /** Stripe publishable key. */
  STRIPE_PK: import.meta.env.VITE_STRIPE_PK ?? 'pk_test_51Tk07NBMnWrnD7CcqlMOPpW2x4icqMehe6z2qjysRJAF1WOprftVJbPiQ8bFw8GSLFLbeKoEq68oJ2VNV1MrvDWZ00zp5jX8Bk',
  /** Price in cents for the transformation. */
  PRICE_CENTS: 499,
  /** Human-readable price display. */
  PRICE_DISPLAY: '$4.99',
  /** Maximum upload size in bytes (10 MB). */
  MAX_FILE_SIZE: 10 * 1024 * 1024,
  /** Stripe Price IDs */
  PRICE_SINGLE: import.meta.env.VITE_STRIPE_PRICE_SINGLE ?? 'price_1Tk1i5Pa52nS2guOEPP8JAxy',
  PRICE_SUBSCRIPTION: import.meta.env.VITE_STRIPE_PRICE_SUBSCRIPTION ?? 'price_1Tk1oKPa52nS2guO6qsXLT6i',
} as const;

/**
 * Stripe.js is ~200 KB of third-party script plus iframes. Loading it lazily
 * (first time the payment form renders) instead of at module scope keeps it
 * off the critical path for every visitor and crawler that never reaches
 * checkout — a measurable LCP/INP win.
 */
let stripePromise: ReturnType<typeof loadStripe> | null = null;
function getStripePromise(): ReturnType<typeof loadStripe> {
  if (!stripePromise) {
    stripePromise = loadStripe(CONFIG.STRIPE_PK);
  }
  return stripePromise;
}

/**
 * Bundled demo file (Stripe Balance History format) for the
 * "Try it with sample data" flow — lets visitors see the full
 * preview experience without exporting anything first.
 */
const SAMPLE_CSV = `balance_transaction_id,created,available_on,currency,gross,fee,net,reporting_category,description
ch_19F8aB2eZvKYlo2C2S6d,2026-06-18 10:30:00,2026-06-20 10:30:00,usd,100.00,3.20,96.80,charge,Invoice Payment for Acme Corp
ref_19F8aB2eZvKYlo2C2S6e,2026-06-18 11:15:00,2026-06-20 11:15:00,usd,-50.00,-1.50,-48.50,refund,Partial Refund for Acme Corp
ch_19F8aB2eZvKYlo2C2S6f,2026-06-18 14:05:00,2026-06-20 14:05:00,usd,250.00,7.55,242.45,charge,Web Development Consulting Services
fee_19F8aB2eZvKYlo2C2S6g,2026-06-18 15:00:00,2026-06-18 15:00:00,usd,-2.00,0.00,-2.00,fee,Stripe Billing Monthly Fee
po_19F8aB2eZvKYlo2C2S6h,2026-06-19 09:00:00,2026-06-19 09:00:00,usd,-290.75,0.00,-290.75,payout,Payout to bank account - *6789
`;

// =============================================================================
// §2  Types — API Response Shapes
// =============================================================================

interface PreviewProfile {
  id: string;
  displayName: string;
}

interface PreviewRow {
  Date: string;
  Description: string;
  Amount?: string;
  Credit?: string;
  Debit?: string;
  Payee?: string;
  Reference?: string;
}

interface PreviewResponse {
  valid: boolean;
  detectedProfile: PreviewProfile | null;
  rowCount: number;
  headers: string[];
  preview: PreviewRow[];
  warnings: { code: string; message: string }[];
  errors: { code: string; message: string }[];
}

interface BatchFileEntry {
  filename: string;
  content: string; // base64 encoded
  mimeType: string;
  rowCount: number;
  byteSize: number;
}

interface BatchResponse {
  batchCount: number;
  totalRows: number;
  detectedProfile: string;
  files: BatchFileEntry[];
}

interface TransformOptions {
  outputFormat: '3col' | '4col' | 'qbo' | 'xero' | 'freeagent';
  amountField: 'net' | 'gross';
  separateFees: boolean;
  /** Inclusive lower date bound (YYYY-MM-DD); empty = no bound. */
  dateFrom: string;
  /** Inclusive upper date bound (YYYY-MM-DD); empty = no bound. */
  dateTo: string;
}

interface DownloadResult {
  filename: string;
  blob: Blob;
  rowCount: number;
  profile: string;
  batchCount: number;
}

type AppPhase =
  | 'IDLE'
  | 'UPLOADING'
  | 'PREVIEW'
  | 'PAYING'
  | 'PROCESSING'
  | 'COMPLETE'
  | 'ERROR';

// =============================================================================
// §3  Utility Functions
// =============================================================================

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function formatNumber(n: number): string {
  return n.toLocaleString('en-US');
}

/** Triggers a browser download from a Blob. */
function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

async function decryptPayloadClient(encryptedStr: string, hexKey: string): Promise<string> {
  const parts = encryptedStr.split('.');
  if (parts.length !== 2) throw new Error('Invalid encrypted payload format.');
  const [ivB64, cipherB64] = parts;

  const ivBin = atob(ivB64);
  const iv = new Uint8Array(ivBin.length);
  for (let i = 0; i < ivBin.length; i++) {
    iv[i] = ivBin.charCodeAt(i);
  }

  const cipherBin = atob(cipherB64);
  const ciphertext = new Uint8Array(cipherBin.length);
  for (let i = 0; i < cipherBin.length; i++) {
    ciphertext[i] = cipherBin.charCodeAt(i);
  }

  const cleanHex = hexKey.trim();
  const binaryKey = new Uint8Array(
    cleanHex.match(/.{1,2}/g)!.map((byte) => parseInt(byte, 16))
  );

  const key = await window.crypto.subtle.importKey(
    'raw',
    binaryKey,
    { name: 'AES-GCM' },
    false,
    ['decrypt']
  );

  const decryptedBuffer = await window.crypto.subtle.decrypt(
    { name: 'AES-GCM', iv },
    key,
    ciphertext
  );

  return new TextDecoder().decode(decryptedBuffer);
}

// =============================================================================
// §4  API Client Functions
// =============================================================================

/**
 * POST /api/transform/preview — sends the CSV file for format detection
 * and 5-row preview generation. No payment required.
 */
async function fetchPreview(
  file: File,
  options: TransformOptions,
): Promise<PreviewResponse> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('options', JSON.stringify(options));

  const res = await fetch(`${CONFIG.API_BASE}/api/transform/preview`, {
    method: 'POST',
    body: formData,
  });

  if (!res.ok) {
    const body = (await res.json().catch(() => ({ error: 'Network error.' }))) as any;
    throw new Error(body.error ?? `Server error ${res.status}`);
  }

  return res.json() as Promise<PreviewResponse>;
}

/**
 * Creates a Stripe PaymentIntent via our Worker (or a lightweight
 * backend endpoint). Returns the client secret.
 *
 * Note: In production, this would hit POST /api/create-payment-intent
 * on the Worker. For the architecture demo, we define the contract here.
 */
async function createPaymentIntent(): Promise<string> {
  const res = await fetch(`${CONFIG.API_BASE}/api/create-payment-intent`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      amount: CONFIG.PRICE_CENTS,
      currency: 'usd',
    }),
  });

  if (!res.ok) {
    const body = (await res.json().catch(() => ({ error: 'Payment setup failed.' }))) as any;
    throw new Error(body.error ?? 'Could not create payment intent.');
  }

  const data = (await res.json()) as { clientSecret: string };
  return data.clientSecret;
}

/**
 * POST /api/transform/full — sends the cached CSV file with the
 * X-Payment-Token header for server-side payment verification.
 *
 * Handles two response shapes:
 *   1. Single file: raw CSV body with Content-Disposition header
 *   2. Batch files: JSON with base64-encoded file array
 */
async function fetchFullTransform(
  file: File,
  authToken: string,
  options: TransformOptions,
  isSubscription: boolean = false,
): Promise<DownloadResult> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('options', JSON.stringify(options));

  const headers: Record<string, string> = {};
  if (isSubscription) {
    headers['X-Subscription-Key'] = authToken;
  } else {
    headers['X-Payment-Token'] = authToken;
  }

  const res = await fetch(`${CONFIG.API_BASE}/api/transform/full`, {
    method: 'POST',
    headers,
    body: formData,
  });

  if (!res.ok) {
    const body = (await res.json().catch(() => ({ error: 'Transformation failed.' }))) as any;
    throw new Error(body.error ?? `Transform error ${res.status}`);
  }

  const contentType = res.headers.get('Content-Type') ?? '';

  // --- Single file response: raw CSV/QBO body ---
  if (contentType.includes('text/csv') || contentType.includes('application/x-ofx')) {
    const disposition = res.headers.get('Content-Disposition') ?? '';
    const filenameMatch = disposition.match(/filename="?([^";\s]+)"?/);
    const filename = filenameMatch?.[1] ?? 'ledgerpass_output.csv';
    const rowCount = parseInt(res.headers.get('X-LedgerPass-Rows') ?? '0', 10);
    const profile = res.headers.get('X-LedgerPass-Profile') ?? 'unknown';

    const blob = await res.blob();

    return { filename, blob, rowCount, profile, batchCount: 1 };
  }

  // --- Batch response: JSON with base64 files ---
  const batch = (await res.json()) as BatchResponse;

  // If single file in batch, download directly
  if (batch.files.length === 1) {
    const f = batch.files[0];
    const decoded = atob(f.content);
    const bytes = new Uint8Array(decoded.length);
    for (let i = 0; i < decoded.length; i++) bytes[i] = decoded.charCodeAt(i);
    const blob = new Blob([bytes], { type: f.mimeType });

    return {
      filename: f.filename,
      blob,
      rowCount: f.rowCount,
      profile: batch.detectedProfile,
      batchCount: 1,
    };
  }

  // Multiple files — create a ZIP using client-side JSZip assembly.
  const JSZip = (await import('jszip')).default;
  const zip = new JSZip();
  for (const file of batch.files) {
    const decoded = atob(file.content);
    const bytes = new Uint8Array(decoded.length);
    for (let i = 0; i < decoded.length; i++) {
      bytes[i] = decoded.charCodeAt(i);
    }
    zip.file(file.filename, bytes);
  }

  const zipBlob = await zip.generateAsync({ type: 'blob' });

  return {
    filename: 'ledgerpass_output_batched.zip',
    blob: zipBlob,
    rowCount: batch.totalRows,
    profile: batch.detectedProfile,
    batchCount: batch.batchCount,
  };
}

// =============================================================================
// §5  CheckoutForm — Stripe Elements Payment Component
// =============================================================================

interface MockCheckoutFormProps {
  onPaymentSuccess: (paymentIntentId: string) => void;
  onPaymentError: (error: string) => void;
}

function MockCheckoutForm({ onPaymentSuccess, onPaymentError }: MockCheckoutFormProps) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [cardNumber, setCardNumber] = useState('');
  const [expiry, setExpiry] = useState('');
  const [cvc, setCvc] = useState('');
  const [zip, setZip] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!cardNumber || !expiry || !cvc || !zip) {
      onPaymentError('Please fill in all simulated card details.');
      return;
    }

    setIsProcessing(true);

    // Simulate Stripe payment processing latency
    setTimeout(() => {
      setIsProcessing(false);
      onPaymentSuccess('pi_mock_123_secret_mock_456');
    }, 1500);
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="ll-stripe-element-container">
        <div className="ll-form-alert">
          <strong>Mock Development Mode Active</strong><br />
          Use any mock details to simulate payment (e.g. Card 4242... Expiry 12/30 CVC 123 Zip 90210).
        </div>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div className="ll-field-group">
            <label htmlFor="mock-card-number" className="ll-field-label">Card Number</label>
            <input
              id="mock-card-number"
              type="text"
              placeholder="4242 4242 4242 4242"
              value={cardNumber}
              onChange={(e) => setCardNumber(e.target.value)}
              className="ll-field-input"
            />
          </div>
          <div style={{ display: 'flex', gap: '16px' }}>
            <div style={{ flex: 1 }} className="ll-field-group">
              <label htmlFor="mock-card-expiry" className="ll-field-label">Expiration</label>
              <input
                id="mock-card-expiry"
                type="text"
                placeholder="MM/YY"
                value={expiry}
                onChange={(e) => setExpiry(e.target.value)}
                className="ll-field-input"
              />
            </div>
            <div style={{ flex: 1 }} className="ll-field-group">
              <label htmlFor="mock-card-cvc" className="ll-field-label">CVC</label>
              <input
                id="mock-card-cvc"
                type="text"
                placeholder="123"
                value={cvc}
                onChange={(e) => setCvc(e.target.value)}
                className="ll-field-input"
              />
            </div>
            <div style={{ flex: 1 }} className="ll-field-group">
              <label htmlFor="mock-card-zip" className="ll-field-label">ZIP Code</label>
              <input
                id="mock-card-zip"
                type="text"
                placeholder="90210"
                value={zip}
                onChange={(e) => setZip(e.target.value)}
                className="ll-field-input"
              />
            </div>
          </div>
        </div>
      </div>

      <button
        type="submit"
        className="ll-pay-btn"
        disabled={isProcessing}
      >
        {isProcessing ? (
          <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
            <span className="ll-spinner" />
            Simulating payment…
          </span>
        ) : (
          <>Pay {CONFIG.PRICE_DISPLAY} & Download (Simulated)</>
        )}
      </button>

      <div className="ll-pay-secure">
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
          <path
            d="M9.5 5.5H2.5C2.22386 5.5 2 5.72386 2 6V10C2 10.2761 2.22386 10.5 2.5 10.5H9.5C9.77614 10.5 10 10.2761 10 10V6C10 5.72386 9.77614 5.5 9.5 5.5Z"
            stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"
          />
          <path
            d="M3.5 5.5V3.5C3.5 2.83696 3.76339 2.20107 4.23223 1.73223C4.70107 1.26339 5.33696 1 6 1C6.66304 1 7.29893 1.26339 7.76777 1.73223C8.23661 2.20107 8.5 2.83696 8.5 3.5V5.5"
            stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"
          />
        </svg>
        Secured by Stripe · Your financial data never touches our servers
      </div>
    </form>
  );
}

interface CheckoutFormProps {
  onPaymentSuccess: (paymentIntentId: string) => void;
  onPaymentError: (error: string) => void;
  isProcessing: boolean;
  setIsProcessing: (v: boolean) => void;
}

function CheckoutForm({
  onPaymentSuccess,
  onPaymentError,
  isProcessing,
  setIsProcessing,
}: CheckoutFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [elementReady, setElementReady] = useState(false);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      if (!stripe || !elements) {
        onPaymentError('Stripe has not loaded yet. Please wait.');
        return;
      }

      setIsProcessing(true);

      // Step 1: Validate the Payment Element form
      const { error: submitError } = await elements.submit();
      if (submitError) {
        onPaymentError(submitError.message ?? 'Form validation failed.');
        setIsProcessing(false);
        return;
      }

      // Step 2: Confirm the PaymentIntent WITHOUT redirect.
      // `redirect: 'if_required'` keeps the user on this page for card payments.
      // Only bank-redirect methods (iDEAL, etc.) would navigate away.
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/?payment=complete`,
        },
        redirect: 'if_required',
      });

      if (error) {
        onPaymentError(error.message ?? 'Payment failed. Please try again.');
        setIsProcessing(false);
        return;
      }

      if (paymentIntent && paymentIntent.status === 'succeeded') {
        onPaymentSuccess(paymentIntent.id);
        return;
      }

      if (paymentIntent && paymentIntent.status === 'requires_action') {
        // 3D Secure or additional auth — Stripe handles this automatically
        // via the Elements overlay. If we reach here, the action failed.
        onPaymentError('Additional authentication was required but could not be completed.');
        setIsProcessing(false);
        return;
      }

      onPaymentError('Unexpected payment status. Please try again.');
      setIsProcessing(false);
    },
    [stripe, elements, onPaymentSuccess, onPaymentError, setIsProcessing],
  );

  return (
    <form onSubmit={handleSubmit}>
      <div className="ll-stripe-element-container">
        <PaymentElement
          onReady={() => setElementReady(true)}
          options={{
            layout: 'tabs',
          }}
        />
      </div>

      <button
        type="submit"
        className="ll-pay-btn"
        disabled={!stripe || !elementReady || isProcessing}
      >
        {isProcessing ? (
          <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
            <span className="ll-spinner" />
            Processing payment…
          </span>
        ) : (
          <>Pay {CONFIG.PRICE_DISPLAY} & Download</>
        )}
      </button>

      <div className="ll-pay-secure">
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
          <path
            d="M9.5 5.5H2.5C2.22386 5.5 2 5.72386 2 6V10C2 10.2761 2.22386 10.5 2.5 10.5H9.5C9.77614 10.5 10 10.2761 10 10V6C10 5.72386 9.77614 5.5 9.5 5.5Z"
            stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"
          />
          <path
            d="M3.5 5.5V3.5C3.5 2.83696 3.76339 2.20107 4.23223 1.73223C4.70107 1.26339 5.33696 1 6 1C6.66304 1 7.29893 1.26339 7.76777 1.73223C8.23661 2.20107 8.5 2.83696 8.5 3.5V5.5"
            stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"
          />
        </svg>
        Secured by Stripe · Your financial data never touches our servers
      </div>
    </form>
  );
}

// =============================================================================
// §6  Main Application Component
// =============================================================================

export default function App() {
  // -------------------------------------------------------------------------
  // Core State
  // -------------------------------------------------------------------------
  const [phase, setPhase] = useState<AppPhase>('IDLE');
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);
  const [activeModal, setActiveModal] = useState<'privacy' | 'terms' | 'restore' | null>(null);
  const [subscriptionKey, setSubscriptionKey] = useState<string | null>(() => {
    return localStorage.getItem('ll_subscription_key');
  });
  const [isSubscriptionActive, setIsSubscriptionActive] = useState<boolean>(false);
  const [subVerifyLoading, setSubVerifyLoading] = useState<boolean>(false);
  const [inputKey, setInputKey] = useState('');
  const [modalError, setModalError] = useState<string | null>(null);
  const [restoreMode, setRestoreMode] = useState<'key' | 'email'>('key');
  const [recoverEmail, setRecoverEmail] = useState('');
  const [recoverSuccess, setRecoverSuccess] = useState(false);
  const [recoverLoading, setRecoverLoading] = useState(false);
  /** 
   * THE CRITICAL INVARIANT: This File reference persists across the
   * Stripe Elements overlay. React state survives DOM overlays.
   */
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<PreviewResponse | null>(null);
  const [options, setOptions] = useState<TransformOptions>({
    outputFormat: '3col',
    amountField: 'net',
    separateFees: false,
    dateFrom: '',
    dateTo: '',
  });
  const [downloadResult, setDownloadResult] = useState<DownloadResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isPayProcessing, setIsPayProcessing] = useState(false);

  // Stripe Elements state
  const [clientSecret, setClientSecret] = useState<string | null>(null);

  // Drag state
  const [isDragActive, setIsDragActive] = useState(false);
  const dropzoneRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const downloadingTokens = useRef(new Set<string>());

  // Programmatic SEO solution state
  const [seoSolution, setSeoSolution] = useState<SeoSolution | null>(null);

  useEffect(() => {
    // Prerendered solution pages live at /solutions/<slug>/; the legacy
    // ?sol=<slug> query form is kept as a fallback for old links.
    const pathMatch = window.location.pathname.match(/^\/solutions\/([\w-]+)\/?$/);
    const params = new URLSearchParams(window.location.search);
    const solKey = pathMatch?.[1] ?? params.get('sol');
    if (solKey && seoSolutions[solKey]) {
      const sol = seoSolutions[solKey];
      setSeoSolution(sol);
      // Dynamically update page title statefully
      document.title = `${sol.title} — LedgerPass`;
    }
  }, []);

  const verifySubscriptionKey = useCallback(async (key: string): Promise<{ success: boolean; error?: string }> => {
    setSubVerifyLoading(true);
    try {
      const res = await fetch(`${CONFIG.API_BASE}/api/verify-subscription`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ licenseKey: key }),
      });
      if (res.ok) {
        const data = await res.json() as { valid: boolean; error?: string };
        if (data.valid) {
          setIsSubscriptionActive(true);
          return { success: true };
        } else {
          // Key was explicitly declared invalid by the server (expired or tampered)
          localStorage.removeItem('ll_subscription_key');
          setSubscriptionKey(null);
          setIsSubscriptionActive(false);
          return { success: false, error: data.error || 'Invalid key.' };
        }
      }
      setIsSubscriptionActive(false);
      return { success: false, error: 'Verification server returned an error.' };
    } catch (err) {
      console.error('Failed to verify subscription:', err);
      setIsSubscriptionActive(false);
      return { success: false, error: 'Network error. Please check your connection.' };
    } finally {
      setSubVerifyLoading(false);
    }
  }, []);

  useEffect(() => {
    const handleHash = async () => {
      const hash = window.location.hash;
      if (hash && hash.startsWith('#')) {
        const hashParams = new URLSearchParams(hash.substring(1));
        const licenseFromHash = hashParams.get('license');
        if (licenseFromHash && licenseFromHash.startsWith('LP_SUB_')) {
          localStorage.setItem('ll_subscription_key', licenseFromHash);
          setSubscriptionKey(licenseFromHash);
          window.history.replaceState({}, document.title, window.location.pathname + window.location.search);
          await verifySubscriptionKey(licenseFromHash);
          return;
        }
      }

      const storedKey = localStorage.getItem('ll_subscription_key');
      if (storedKey) {
        await verifySubscriptionKey(storedKey);
      }
    };

    handleHash();
  }, [verifySubscriptionKey]);

  const handleRestoreSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setModalError(null);
      if (!inputKey.trim()) {
        setModalError('Please enter a license key.');
        return;
      }
      const cleanKey = inputKey.trim();
      if (!cleanKey.startsWith('LP_SUB_')) {
        setModalError('Invalid key format. License keys start with "LP_SUB_".');
        return;
      }

      const result = await verifySubscriptionKey(cleanKey);
      if (result.success) {
        localStorage.setItem('ll_subscription_key', cleanKey);
        setSubscriptionKey(cleanKey);
        setActiveModal(null);
        setInputKey('');
      } else {
        setModalError(result.error || 'Could not verify subscription.');
      }
    },
    [inputKey, verifySubscriptionKey],
  );

  const handleRecoverSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setModalError(null);
      setRecoverSuccess(false);

      const email = recoverEmail.trim();
      if (!email || !email.includes('@')) {
        setModalError('Please enter a valid email address.');
        return;
      }

      setRecoverLoading(true);
      try {
        const res = await fetch(`${CONFIG.API_BASE}/api/recover-subscription`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email }),
        });

        if (res.ok) {
          setRecoverSuccess(true);
          setRecoverEmail('');
        } else {
          const data = (await res.json().catch(() => ({}))) as any;
          setModalError(data.error || 'Failed to submit recovery request. Please try again.');
        }
      } catch (err) {
        console.error('Failed to submit recover request:', err);
        setModalError('Network error. Please check your connection and try again.');
      } finally {
        setRecoverLoading(false);
      }
    },
    [recoverEmail],
  );

  const handleDeactivateSubscription = useCallback(() => {
    localStorage.removeItem('ll_subscription_key');
    setSubscriptionKey(null);
    setIsSubscriptionActive(false);
  }, []);

  const handleManageBilling = useCallback(async () => {
    const key = localStorage.getItem('ll_subscription_key');
    if (!key) return;

    setError(null);
    setPhase('PROCESSING');

    try {
      const res = await fetch(`${CONFIG.API_BASE}/api/create-portal-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ licenseKey: key }),
      });

      if (!res.ok) {
        const body = (await res.json().catch(() => ({ error: 'Could not create billing portal session.' }))) as any;
        throw new Error(body.error ?? 'Portal error.');
      }

      const data = (await res.json()) as { url: string };
      window.location.href = data.url;
    } catch (err: any) {
      setError(err.message ?? 'Failed to open billing portal.');
      setPhase('IDLE');
    }
  }, []);

  useEffect(() => {
    if (activeModal === 'restore') {
      setRestoreMode('key');
      setRecoverEmail('');
      setRecoverSuccess(false);
      setModalError(null);
      setInputKey('');
    }
  }, [activeModal]);

  // Auto-scroll on state transitions to optimize the user flow
  useEffect(() => {
    // 1. When a file is loaded (IDLE -> PREVIEW)
    if (phase === 'PREVIEW' && csvFile) {
      setTimeout(() => {
        const target = document.querySelector('.ll-file-info') || document.querySelector('.ll-options');
        if (target) {
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
    // 2. When starting in-page payment (PREVIEW -> PAYING)
    if (phase === 'PAYING' && clientSecret) {
      setTimeout(() => {
        const target = document.querySelector('.ll-pay-card');
        if (target) {
          target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }
    // 3. When download is complete (PAYING/PROCESSING -> COMPLETE)
    if (phase === 'COMPLETE' && downloadResult) {
      setTimeout(() => {
        const target = document.querySelector('.ll-success');
        if (target) {
          target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }
  }, [phase, csvFile, clientSecret, downloadResult]);

  // -------------------------------------------------------------------------
  // Stripe Elements options — memoized on clientSecret
  // -------------------------------------------------------------------------
  const stripeElementsOptions: StripeElementsOptions | null = useMemo(() => {
    if (!clientSecret) return null;
    return {
      clientSecret,
      appearance: {
        theme: 'night' as const,
        variables: {
          colorPrimary: '#34D399',
          colorBackground: '#111827',
          colorText: '#F1F5F9',
          colorDanger: '#F87171',
          fontFamily: "'Inter', sans-serif",
          borderRadius: '10px',
          spacingUnit: '4px',
          fontSizeBase: '14px',
          fontWeightNormal: '500',
        },
        rules: {
          '.Input': {
            border: '1px solid rgba(148, 163, 184, 0.12)',
            boxShadow: 'none',
            backgroundColor: '#1A2235',
          },
          '.Input:focus': {
            border: '1px solid #34D399',
            boxShadow: '0 0 0 3px rgba(52, 211, 153, 0.12)',
          },
          '.Label': {
            color: '#94A3B8',
            fontWeight: '600',
            fontSize: '12px',
            letterSpacing: '0.3px',
            textTransform: 'uppercase' as const,
          },
          '.Tab': {
            border: '1px solid rgba(148, 163, 184, 0.12)',
            backgroundColor: '#1A2235',
          },
          '.Tab--selected': {
            border: '1px solid #34D399',
            backgroundColor: 'rgba(52, 211, 153, 0.08)',
          },
        },
      },
    };
  }, [clientSecret]);

  // -------------------------------------------------------------------------
  // File Handling
  // -------------------------------------------------------------------------

  const handleFile = useCallback(
    async (file: File) => {
      setError(null);

      // Validate file type
      if (!file.name.toLowerCase().endsWith('.csv')) {
        setError('Please upload a .csv file.');
        return;
      }

      // Validate size
      if (file.size > CONFIG.MAX_FILE_SIZE) {
        setError(`File exceeds ${formatBytes(CONFIG.MAX_FILE_SIZE)} limit.`);
        return;
      }

      // Store file reference in React state — THIS IS THE KEY INVARIANT.
      // This reference will persist through the entire Stripe Elements
      // payment modal lifecycle.
      setCsvFile(file);
      setPhase('UPLOADING');
      setPreview(null);
      setDownloadResult(null);

      try {
        const result = await fetchPreview(file, options);

        if (!result.valid) {
          const errorMsg =
            result.errors.map((e) => e.message).join(' ') ||
            'File validation failed.';
          setError(errorMsg);
          setPhase('ERROR');
          return;
        }

        setPreview(result);
        setPhase('PREVIEW');

        // Pre-create the PaymentIntent as soon as preview is confirmed.
        // This minimizes latency when the user clicks "Pay".
        if (!isSubscriptionActive) {
          try {
            const secret = await createPaymentIntent();
            setClientSecret(secret);
          } catch (piError: any) {
            // Non-fatal: we can retry when user clicks pay
            console.warn('Pre-fetch PaymentIntent failed:', piError);
          }
        }
      } catch (err: any) {
        setError(err.message ?? 'Failed to process file.');
        setPhase('ERROR');
      }
    },
    [options, isSubscriptionActive],
  );

  const handleDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragActive(false);

      const file = e.dataTransfer?.files?.[0];
      if (file) handleFile(file);
    },
    [handleFile],
  );

  const handleDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    // Only deactivate if leaving the dropzone itself
    if (dropzoneRef.current && !dropzoneRef.current.contains(e.relatedTarget as Node)) {
      setIsDragActive(false);
    }
  }, []);

  const handleInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) handleFile(file);
      // Reset input so re-uploading the same file triggers onChange
      if (e.target) e.target.value = '';
    },
    [handleFile],
  );

  const handleTrySample = useCallback(() => {
    const file = new File([SAMPLE_CSV], 'sample_stripe_balance_history.csv', {
      type: 'text/csv',
    });
    handleFile(file);
  }, [handleFile]);

  const removeFile = useCallback(() => {
    setCsvFile(null);
    setPreview(null);
    setPhase('IDLE');
    setError(null);
    setClientSecret(null);
    setDownloadResult(null);
  }, []);

  // -------------------------------------------------------------------------
  // Options Changes — re-run preview with new options
  // -------------------------------------------------------------------------
  const handleOptionChange = useCallback(
    async (newOptions: Partial<TransformOptions>) => {
      const merged = { ...options, ...newOptions };
      setOptions(merged);

      if (csvFile && phase === 'PREVIEW') {
        setPhase('UPLOADING');
        try {
          const result = await fetchPreview(csvFile, merged);
          if (result.valid) {
            setPreview(result);
            setPhase('PREVIEW');
          } else {
            setError(result.errors.map((e) => e.message).join(' '));
            setPhase('ERROR');
          }
        } catch (err: any) {
          setError(err.message);
          setPhase('ERROR');
        }
      }
    },
    [csvFile, options, phase],
  );

  // -------------------------------------------------------------------------
  // Payment Callbacks
  // -------------------------------------------------------------------------

  const handlePaymentSuccess = useCallback(
    async (paymentIntentId: string) => {
      if (!csvFile) {
        setError('File reference lost. Please re-upload.');
        setPhase('ERROR');
        setIsPayProcessing(false);
        return;
      }

      setPhase('PROCESSING');

      try {
        // CRITICAL PATH: The csvFile reference is still in React state.
        // We re-send it to the Worker with the payment token.
        const result = await fetchFullTransform(csvFile, paymentIntentId, options);

        // Trigger browser download
        downloadBlob(result.blob, result.filename);

        setDownloadResult(result);
        setPhase('COMPLETE');
      } catch (err: any) {
        setError(err.message ?? 'Download failed after payment.');
        setPhase('ERROR');
      } finally {
        setIsPayProcessing(false);
      }
    },
    [csvFile, options],
  );

  const handleSubscriptionDownload = useCallback(async () => {
    if (!csvFile || !subscriptionKey) {
      setError('File reference or subscription key lost. Please re-upload or restore subscription.');
      return;
    }

    setError(null);
    setPhase('PROCESSING');

    try {
      const result = await fetchFullTransform(csvFile, subscriptionKey, options, true);

      // Trigger browser download
      downloadBlob(result.blob, result.filename);

      setDownloadResult(result);
      setPhase('COMPLETE');
    } catch (err: any) {
      setError(err.message ?? 'Transformation failed.');
      setPhase('ERROR');
    }
  }, [csvFile, subscriptionKey, options]);

  const handlePaymentError = useCallback((errorMessage: string) => {
    setError(errorMessage);
    // Don't change phase — stay in PAYING so user can retry
  }, []);

  const initiatePayment = useCallback(async () => {
    setError(null);
    setPhase('PAYING');

    // If we don't have a clientSecret yet, create the PaymentIntent now
    if (!clientSecret) {
      try {
        const secret = await createPaymentIntent();
        setClientSecret(secret);
      } catch (err: any) {
        setError(err.message ?? 'Could not initialize payment.');
        setPhase('PREVIEW');
        return;
      }
    }
  }, [clientSecret]);

  // -------------------------------------------------------------------------
  // Path B: Ephemeral KV Download Handler & Mount Trigger
  // -------------------------------------------------------------------------

  const handleStripeCheckoutRedirect = useCallback(async (priceId: string) => {
    if (!csvFile) {
      setError('File reference lost. Please re-upload.');
      return;
    }
    setError(null);
    setPhase('PROCESSING');

    try {
      const formData = new FormData();
      formData.append('file', csvFile);
      formData.append('options', JSON.stringify(options));
      formData.append('priceId', priceId);
      formData.append('mode', priceId === CONFIG.PRICE_SUBSCRIPTION ? 'subscription' : 'payment');

      const existingKey = localStorage.getItem('ll_subscription_key');
      if (existingKey) {
        formData.append('licenseKey', existingKey);
      }

      const res = await fetch(`${CONFIG.API_BASE}/api/prepare-checkout`, {
        method: 'POST',
        body: formData,
      });

      if (!res.ok) {
        const body = (await res.json().catch(() => ({ error: 'Checkout preparation failed.' }))) as any;
        throw new Error(body.error ?? 'Failed to prepare checkout.');
      }

      const data = await res.json() as { checkoutUrl: string; sessionToken: string; decryptionKey: string };
      
      // Save key in sessionStorage before redirect
      sessionStorage.setItem('ll_key_' + data.sessionToken, data.decryptionKey);

      // Redirect user to Stripe Checkout
      window.location.href = data.checkoutUrl;
    } catch (err: any) {
      setError(err.message ?? 'Failed to initiate checkout.');
      setPhase('PREVIEW');
    }
  }, [csvFile, options]);

  const handleKvDownload = useCallback(async (token: string, isMock: boolean) => {
    try {
      if (isMock) {
        // Trigger mock webhook payment completion for local testing
        const mockWebhookBody = {
          id: 'evt_mock',
          type: 'payment_intent.succeeded',
          data: {
            object: {
              id: 'pi_mock_123_secret_mock_456',
              status: 'succeeded',
              metadata: {
                session_token: token,
              },
            },
          },
        };
        await fetch(`${CONFIG.API_BASE}/api/webhook/stripe`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'stripe-signature': 't=123,v1=mock_sig',
          },
          body: JSON.stringify(mockWebhookBody),
        });
      }

      // 1. Retrieve the decryption key from sessionStorage
      const decryptionKey = sessionStorage.getItem('ll_key_' + token);
      if (!decryptionKey) {
        throw new Error('Decryption key not found. Please ensure session storage is enabled.');
      }

      // Fetch pre-computed ciphertext from Worker KV (with polling to resolve webhook latency races)
      let res: Response | null = null;
      let retries = 5;
      while (retries > 0) {
        res = await fetch(`${CONFIG.API_BASE}/api/download/${token}`);
        if (res.status === 402) {
          await new Promise((resolve) => setTimeout(resolve, 2000));
          retries--;
        } else {
          break;
        }
      }

      if (!res || !res.ok) {
        const body = (await res?.json().catch(() => ({ error: 'Download retrieval failed.' }))) as any;
        throw new Error(body?.error ?? 'Could not retrieve transformed files.');
      }

      // Auto-capture subscription license key if returned in headers
      const subKeyHeader = res.headers.get('X-Subscription-Key');
      if (subKeyHeader) {
        localStorage.setItem('ll_subscription_key', subKeyHeader);
        setSubscriptionKey(subKeyHeader);
        setIsSubscriptionActive(true);
      }

      // 2. Read the response as text (which is "ivB64.cipherB64")
      const encryptedStr = await res.text();

      // 3. Decrypt the payload using Web Crypto API in browser
      const decryptedStr = await decryptPayloadClient(encryptedStr, decryptionKey);

      // 4. Parse the decrypted JSON
      const payload = JSON.parse(decryptedStr) as {
        files: { filename: string; content: string; mimeType: string; rowCount: number }[];
      };

      // 5. Build file/ZIP and download
      let filename: string;
      let blob: Blob;

      if (payload.files.length === 1) {
        const f = payload.files[0];
        blob = new Blob([f.content], { type: f.mimeType });
        filename = f.filename;
      } else {
        // Assemble ZIP client-side
        const JSZip = (await import('jszip')).default;
        const zip = new JSZip();
        for (const f of payload.files) {
          zip.file(f.filename, f.content);
        }
        blob = await zip.generateAsync({ type: 'blob' });
        filename = 'ledgerpass_output_batched.zip';
      }

      downloadBlob(blob, filename);

      setDownloadResult({
        filename,
        blob,
        rowCount: payload.files.reduce((acc, f) => acc + f.rowCount, 0),
        profile: 'Generic / Stripe',
        batchCount: payload.files.length,
      });
      setPhase('COMPLETE');

      // 6. Clean up sessionStorage key
      sessionStorage.removeItem('ll_key_' + token);
      
      // Clear URL params and hash so refresh doesn't trigger again after successful retrieval
      window.history.replaceState({}, document.title, window.location.pathname);
    } catch (err: any) {
      setError(err.message ?? 'Failed to retrieve files.');
      setPhase('ERROR');
    }
  }, []);

  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const payment = params.get('payment');
    const token = params.get('session_token');
    const isMock = params.get('mock_checkout') === 'true';

    if (payment === 'success' && token) {
      if (downloadingTokens.current.has(token)) {
        return;
      }
      downloadingTokens.current.add(token);

      // Extract key from URL hash fragment if present (fallback)
      const hash = window.location.hash;
      if (hash && hash.startsWith('#')) {
        const hashParams = new URLSearchParams(hash.substring(1));
        const keyFromHash = hashParams.get('key');
        if (keyFromHash) {
          sessionStorage.setItem('ll_key_' + token, keyFromHash);
        }
      }

      // (Cleanup url replaced code removed from here; deferred to successful KV download completion)

      setPhase('PROCESSING');
      handleKvDownload(token, isMock);
    }
  }, [handleKvDownload]);

  // -------------------------------------------------------------------------
  // Reset to start over
  // -------------------------------------------------------------------------
  const resetApp = useCallback(() => {
    setCsvFile(null);
    setPreview(null);
    setPhase('IDLE');
    setError(null);
    setClientSecret(null);
    setDownloadResult(null);
    setIsPayProcessing(false);
  }, []);

  // =========================================================================
  // RENDER
  // =========================================================================

  return (
    <div className="ll-app">
      {/* ─── Header ─── */}
      <header className="ll-header">
        <div className="ll-header-inner" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <BrandLogo height={48} />
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {isSubscriptionActive ? (
              <div 
                className="ll-detected" 
                style={{ 
                  background: 'rgba(52, 211, 153, 0.08)', 
                  borderColor: 'rgba(52, 211, 153, 0.2)',
                  color: '#34D399',
                  padding: '6px 12px',
                  borderRadius: 'var(--ll-radius-full)',
                  fontSize: '13px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  margin: 0
                }}
              >
                <span style={{ display: 'inline-block', width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#34D399' }} />
                Subscription Active
                <button 
                  onClick={handleManageBilling}
                  style={{ 
                    background: 'none', 
                    border: 'none', 
                    color: '#60A5FA', 
                    cursor: 'pointer', 
                    fontSize: '11px',
                    marginLeft: '8px',
                    padding: '2px 4px',
                    textDecoration: 'underline'
                  }}
                >
                  Manage Billing
                </button>
                <span style={{ color: 'rgba(52, 211, 153, 0.3)', fontSize: '11px', marginLeft: '4px' }}>|</span>
                <button 
                  onClick={handleDeactivateSubscription}
                  style={{ 
                    background: 'none', 
                    border: 'none', 
                    color: '#F87171', 
                    cursor: 'pointer', 
                    fontSize: '11px',
                    marginLeft: '4px',
                    padding: '2px 4px',
                    textDecoration: 'underline'
                  }}
                >
                  Deactivate
                </button>
              </div>
            ) : (
              <button 
                onClick={() => setActiveModal('restore')}
                className="ll-pay-btn-link"
                style={{ fontSize: '13.5px', fontWeight: 500 }}
              >
                Already Subscribed?
              </button>
            )}
          </div>
        </div>
      </header>

      {/* ─── Main Content ─── */}
      <main className="ll-main">
        <div className="ll-container">
          {!csvFile && (phase === 'IDLE' || phase === 'UPLOADING' || phase === 'ERROR') ? (
            <div className="ll-split-layout">
              {/* Left Column: Value propositions & How It Works */}
              <div className="ll-split-left">
                {seoSolution ? (
                  <section className="ll-hero" style={{ textAlign: 'left', marginBottom: 'var(--ll-space-sm)' }}>
                    <h1 style={{ fontSize: '36px', lineHeight: 1.15, marginBottom: '12px' }}>
                      {seoSolution.heroTitle}
                    </h1>
                    <p className="ll-hero-subhook" style={{ margin: 0, fontSize: '14.5px', maxWidth: 'none', lineHeight: 1.5 }}>
                      {seoSolution.heroSub}
                    </p>
                  </section>
                ) : (
                  <section className="ll-hero" style={{ textAlign: 'left', marginBottom: 'var(--ll-space-sm)' }}>
                    <h1 style={{ fontSize: '38px', lineHeight: 1.15, marginBottom: '12px' }}>
                      Convert Stripe, Shopify & PayPal CSVs into{' '}
                      <span className="ll-accent">QuickBooks-Ready Files.</span>
                    </h1>
                    <p className="ll-hero-subhook" style={{ margin: 0, fontSize: '14.5px', maxWidth: 'none' }}>
                      Turn Stripe, Shopify, PayPal, Square, Wise, Venmo, and Etsy exports into
                      import-ready bank feeds for QuickBooks, Xero, or FreeAgent.
                      100% secure, stateless, and processed entirely in memory with zero data retention.
                    </p>
                  </section>
                )}

                <div className="ll-trust-bar" style={{ justifyContent: 'flex-start', gap: '8px', marginBottom: '24px', flexWrap: 'nowrap' }}>
                  <div className="ll-trust-chip" style={{ fontSize: '11px', padding: '6px 12px' }}>
                    <svg className="ll-trust-chip-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                    Zero Retention
                  </div>
                  <div className="ll-trust-chip" style={{ fontSize: '11px', padding: '6px 12px' }}>
                    <svg className="ll-trust-chip-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
                    Edge-Processed
                  </div>
                  <div className="ll-trust-chip" style={{ fontSize: '11px', padding: '6px 12px' }}>
                    <svg className="ll-trust-chip-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                    QBO Ready
                  </div>
                </div>

                {seoSolution && (
                  <div 
                    className="ll-form-alert" 
                    style={{ 
                      background: 'rgba(59, 130, 246, 0.05)', 
                      border: '1px solid rgba(59, 130, 246, 0.15)', 
                      color: 'var(--ll-text-primary)',
                      padding: '16px',
                      borderRadius: 'var(--ll-radius-lg)',
                      marginBottom: '24px',
                      fontSize: '13px',
                      lineHeight: '1.6'
                    }}
                  >
                    <strong style={{ color: 'var(--ll-accent-emerald)', display: 'block', marginBottom: '6px', fontSize: '13.5px' }}>
                      {seoSolution.atomicTitle}
                    </strong>
                    <span style={{ color: 'var(--ll-text-secondary)' }}>
                      {seoSolution.atomicBody}
                    </span>
                  </div>
                )}

                <div className="ll-features-list">
                  <h2 className="ll-features-heading" style={{ textAlign: 'left', marginBottom: '14px', fontSize: '10.5px' }}>How It Works</h2>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                    <div className="ll-feature-row">
                      <div className="ll-feature-step" style={{ width: '24px', height: '24px', fontSize: '11px', marginBottom: 0 }}>1</div>
                      <div>
                        <h4 className="ll-feature-title-small">Upload CSV</h4>
                        <p className="ll-feature-desc-small">Drop your Stripe, PayPal, or Square CSV export. We auto-detect structures.</p>
                      </div>
                    </div>
                    <div className="ll-feature-row">
                      <div className="ll-feature-step" style={{ width: '24px', height: '24px', fontSize: '11px', marginBottom: 0 }}>2</div>
                      <div>
                        <h4 className="ll-feature-title-small">Choose Format</h4>
                        <p className="ll-feature-desc-small">QuickBooks 3/4-column CSV, QBO Web Connect, Xero, or FreeAgent format — with optional date-range filtering for duplicate-free monthly re-exports.</p>
                      </div>
                    </div>
                    <div className="ll-feature-row">
                      <div className="ll-feature-step" style={{ width: '24px', height: '24px', fontSize: '11px', marginBottom: 0 }}>3</div>
                      <div>
                        <h4 className="ll-feature-title-small">Download QBO</h4>
                        <p className="ll-feature-desc-small">Get your transaction feed instantly. Processed completely in volatile RAM.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column: Dropzone */}
              <div className="ll-split-right">
                <div
                  ref={dropzoneRef}
                  className={`ll-dropzone ${isDragActive ? 'active' : ''}`}
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onClick={() => phase !== 'UPLOADING' && fileInputRef.current?.click()}
                  onKeyDown={(e) => {
                    if (phase !== 'UPLOADING' && (e.key === 'Enter' || e.key === ' ')) {
                      e.preventDefault();
                      fileInputRef.current?.click();
                    }
                  }}
                  role="button"
                  tabIndex={phase === 'UPLOADING' ? -1 : 0}
                  aria-label="Upload CSV file"
                  style={{ minHeight: '340px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv"
                    className="ll-dropzone-input"
                    onChange={handleInputChange}
                    onClick={(e) => e.stopPropagation()}
                    tabIndex={-1}
                    disabled={phase === 'UPLOADING'}
                  />
                  {phase === 'UPLOADING' ? (
                    <div style={{ padding: '24px 0' }}>
                      <span className="ll-spinner ll-spinner-light" style={{ width: 36, height: 36 }} />
                      <div style={{ marginTop: 16, fontSize: 15, fontWeight: 600, color: 'var(--ll-text-primary)' }}>
                        Analyzing file structure…
                      </div>
                      <div style={{ marginTop: 6, fontSize: 13, color: 'var(--ll-text-tertiary)' }}>
                        Validating CSV formats and parsing headers
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="ll-dropzone-icon">
                        <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                      </div>
                      <div className="ll-dropzone-label">
                        Drop your CSV here, or click to browse
                      </div>
                      <div className="ll-dropzone-hint">
                        Supports <strong>Stripe</strong>, <strong>PayPal</strong>,{' '}
                        <strong>Square</strong>, <strong>Shopify</strong>,{' '}
                        <strong>Wise</strong>, <strong>Venmo</strong>,{' '}
                        <strong>Etsy</strong>, and generic bank CSVs · Max 10 MB
                      </div>
                      <button
                        type="button"
                        className="ll-pay-btn-link"
                        // position+zIndex lift the button above the invisible
                        // .ll-dropzone-input overlay that captures dropzone clicks
                        style={{ marginTop: '16px', fontSize: '13px', position: 'relative', zIndex: 2 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleTrySample();
                        }}
                      >
                        No export handy? Try it with sample data →
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ) : null}

          {/* ─── File Loaded State ─── */}
          {csvFile && phase !== 'COMPLETE' && (
            <div className={`ll-dropzone has-file`}>
              <div className="ll-file-info">
                <div className="ll-file-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--ll-accent-emerald)' }}>
                    <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="16" y1="13" x2="8" y2="13" />
                    <line x1="16" y1="17" x2="8" y2="17" />
                    <line x1="10" y1="9" x2="8" y2="9" />
                  </svg>
                </div>
                <div className="ll-file-meta">
                  <div className="ll-file-name">{csvFile.name}</div>
                  <div className="ll-file-size">{formatBytes(csvFile.size)}</div>
                </div>
                {phase !== 'PAYING' && phase !== 'PROCESSING' && (
                  <button
                    className="ll-file-remove"
                    onClick={removeFile}
                    aria-label="Remove file"
                    title="Remove file and start over"
                  >
                    ✕
                  </button>
                )}
              </div>

              {/* Detection Badge */}
              {preview?.detectedProfile && (
                <div className="ll-detected">
                  <span className="ll-detected-icon">✓</span>
                  Detected: {preview.detectedProfile.displayName}
                  <span className="ll-detected-rows">
                    {formatNumber(preview.rowCount)} rows
                  </span>
                </div>
              )}

              {/* Warnings */}
              {preview?.warnings && preview.warnings.length > 0 && (
                <div className="ll-warning-banner" style={{ flexDirection: 'column', alignItems: 'stretch', gap: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 600 }}>
                    <span className="ll-banner-icon">⚠</span>
                    <span>Import Warnings Detected</span>
                  </div>
                  <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '12.5px', listStyleType: 'disc', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    {preview.warnings.map((w, i) => (
                      <li key={i}>{w.message}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* (Dynamic uploading indicator handled inside the dropzone container layout to resolve layout shifts) */}

          {/* ─── Error Banner ─── */}
          {error && (
            <div className="ll-error-banner">
              <span className="ll-banner-icon">✕</span>
              <div>{error}</div>
            </div>
          )}

          {/* ─── Options Bar ─── */}
          {preview && (phase === 'PREVIEW' || phase === 'PAYING') && (
            <div className="ll-options">
              <div className="ll-option-group">
                <label htmlFor="opt-format">Format</label>
                <select
                  id="opt-format"
                  className="ll-select"
                  value={options.outputFormat}
                  onChange={(e) =>
                    handleOptionChange({ outputFormat: e.target.value as TransformOptions['outputFormat'] })
                  }
                  disabled={phase === 'PAYING'}
                >
                  <option value="3col">QuickBooks 3-Column (Date, Desc, Amount)</option>
                  <option value="4col">QuickBooks 4-Column (Date, Desc, Credit, Debit)</option>
                  <option value="qbo">QBO Web Connect (.qbo)</option>
                  <option value="xero">Xero Bank Statement CSV</option>
                  <option value="freeagent">FreeAgent Statement CSV</option>
                </select>
              </div>
              <div className="ll-option-group">
                <label htmlFor="opt-amount">Amount</label>
                <select
                  id="opt-amount"
                  className="ll-select"
                  value={options.amountField}
                  onChange={(e) =>
                    handleOptionChange({ amountField: e.target.value as TransformOptions['amountField'] })
                  }
                  disabled={phase === 'PAYING'}
                >
                  <option value="net">Net (after fees)</option>
                  <option value="gross">Gross (before fees)</option>
                </select>
              </div>
              <div className="ll-option-group">
                <label htmlFor="opt-date-from">From (optional)</label>
                <input
                  id="opt-date-from"
                  type="date"
                  className="ll-select"
                  value={options.dateFrom}
                  onChange={(e) => handleOptionChange({ dateFrom: e.target.value })}
                  disabled={phase === 'PAYING'}
                  title="Exclude transactions before this date — keeps monthly re-exports duplicate-free"
                />
              </div>
              <div className="ll-option-group">
                <label htmlFor="opt-date-to">To (optional)</label>
                <input
                  id="opt-date-to"
                  type="date"
                  className="ll-select"
                  value={options.dateTo}
                  onChange={(e) => handleOptionChange({ dateTo: e.target.value })}
                  disabled={phase === 'PAYING'}
                  title="Exclude transactions after this date"
                />
              </div>
            </div>
          )}

          {/* ─── Preview Table ─── */}
          {preview && preview.preview.length > 0 && (phase === 'PREVIEW' || phase === 'PAYING') && (
            <section className="ll-preview-section">
              <div className="ll-preview-header">
                <span className="ll-preview-title">Output Preview</span>
                <span className="ll-preview-subtitle">
                  Showing {preview.preview.length} of {formatNumber(preview.rowCount)} rows
                </span>
              </div>
              <div className="ll-preview-table-wrap">
                <table className="ll-preview-table">
                  <thead>
                    <tr>
                      {Object.keys(preview.preview[0]).map((col) => (
                        <th key={col}>{col}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.preview.map((row, idx) => (
                      <tr key={idx}>
                        {Object.entries(row).map(([key, val]) => {
                          // Amount coloring
                          let className = '';
                          if (key === 'Amount' || key === 'Credit' || key === 'Debit') {
                            if (val && parseFloat(val) > 0) className = 'll-amount-positive';
                            if (val && parseFloat(val) < 0) className = 'll-amount-negative';
                            if (key === 'Debit' && val) className = 'll-amount-negative';
                            if (key === 'Credit' && val) className = 'll-amount-positive';
                          }
                          return (
                            <td key={key} className={className}>
                              {val || '—'}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {/* ─── Payment Section ─── */}
          {phase === 'PREVIEW' && preview && (
            <section className="ll-pay-section">
              {isSubscriptionActive ? (
                <div style={{ maxWidth: '500px', margin: '0 auto', textAlign: 'center' }}>
                  <div 
                    className="ll-detected" 
                    style={{ 
                      justifyContent: 'center', 
                      background: 'rgba(52, 211, 153, 0.08)', 
                      borderColor: 'rgba(52, 211, 153, 0.2)',
                      color: '#34D399',
                      padding: '12px 20px',
                      fontSize: '14.5px',
                      marginBottom: '20px'
                    }}
                  >
                    <span className="ll-detected-icon">✓</span>
                    Unlimited Subscription Active
                  </div>
                  <button 
                    className="ll-pay-btn" 
                    style={{ width: '100%', padding: '16px' }}
                    onClick={handleSubscriptionDownload}
                  >
                    Convert & Download Full File
                  </button>
                  <div style={{ marginTop: '16px', display: 'flex', gap: '16px', justifyContent: 'center' }}>
                    <button 
                      className="ll-pay-btn-link" 
                      style={{ fontSize: '13px' }}
                      onClick={handleManageBilling}
                    >
                      Manage Billing
                    </button>
                    <span style={{ color: 'var(--ll-text-tertiary)', fontSize: '13px' }}>|</span>
                    <button 
                      className="ll-pay-btn-link" 
                      style={{ fontSize: '13px' }}
                      onClick={() => setActiveModal('restore')}
                    >
                      Change Key
                    </button>
                    <span style={{ color: 'var(--ll-text-tertiary)', fontSize: '13px' }}>|</span>
                    <button 
                      className="ll-pay-btn-link" 
                      style={{ fontSize: '13px', color: '#F87171' }}
                      onClick={handleDeactivateSubscription}
                    >
                      Deactivate
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="ll-pay-container-label">
                    Ready to transform {formatNumber(preview.rowCount)} rows into QBO format
                  </div>
                  <div className="ll-pricing-cards">
                    {/* Single Pass Card */}
                    <div className="ll-pricing-card ll-pricing-card--popular">
                      <div className="ll-pricing-tag">Recommended</div>
                      <h3 className="ll-pricing-title">Single Pass</h3>
                      <div className="ll-pricing-price">$4.99<span className="ll-pricing-period">/one-time</span></div>
                      <p className="ll-pricing-desc">Transform this Stripe CSV export instantly. Secure, stateless, and zero data retention.</p>
                      <button className="ll-pay-btn" onClick={() => handleStripeCheckoutRedirect(CONFIG.PRICE_SINGLE)}>
                        Select Single Pass
                      </button>
                    </div>

                    {/* Subscription Card */}
                    <div className="ll-pricing-card">
                      <h3 className="ll-pricing-title">Unlimited Access</h3>
                      <div className="ll-pricing-price">$14.99<span className="ll-pricing-period">/month</span></div>
                      <p className="ll-pricing-desc">Convert unlimited CSV exports for multiple Stripe accounts or bookkeeping clients.</p>
                      <button className="ll-pay-btn ll-pay-btn--outline" onClick={() => handleStripeCheckoutRedirect(CONFIG.PRICE_SUBSCRIPTION)}>
                        Subscribe Unlimited
                      </button>
                    </div>
                  </div>

                  <div className="ll-pay-alternative-wrap">
                    <button className="ll-pay-btn-link" onClick={initiatePayment}>
                      Prefer to pay directly in-page using card details?
                    </button>
                    <span style={{ color: 'var(--ll-text-tertiary)', margin: '0 8px' }}>·</span>
                    <button className="ll-pay-btn-link" onClick={() => setActiveModal('restore')}>
                      Already subscribed? Restore access
                    </button>
                  </div>

                  <div
                    style={{
                      maxWidth: '560px',
                      margin: '20px auto 0',
                      padding: '12px 16px',
                      borderRadius: 'var(--ll-radius-lg)',
                      background: 'rgba(52, 211, 153, 0.06)',
                      border: '1px solid rgba(52, 211, 153, 0.18)',
                      fontSize: '13px',
                      lineHeight: 1.6,
                      color: 'var(--ll-text-secondary)',
                      textAlign: 'center',
                    }}
                  >
                    <strong style={{ color: '#34D399' }}>Import-success guarantee.</strong>{' '}
                    If your converted file doesn't import into QuickBooks on the first
                    try, email{' '}
                    <a href="mailto:support@getledgerpass.com" style={{ color: '#34D399' }}>
                      support@getledgerpass.com
                    </a>{' '}
                    and we'll fix the file or refund your payment in full.
                  </div>
                </>
              )}

              <div className="ll-pay-secure" style={{ marginTop: isSubscriptionActive ? '20px' : '0' }}>
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path
                    d="M9.5 5.5H2.5C2.22386 5.5 2 5.72386 2 6V10C2 10.2761 2.22386 10.5 2.5 10.5H9.5C9.77614 10.5 10 10.2761 10 10V6C10 5.72386 9.77614 5.5 9.5 5.5Z"
                    stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"
                  />
                  <path
                    d="M3.5 5.5V3.5C3.5 2.83696 3.76339 2.20107 4.23223 1.73223C4.70107 1.26339 5.33696 1 6 1C6.66304 1 7.29893 1.26339 7.76777 1.73223C8.23661 2.20107 8.5 2.83696 8.5 3.5V5.5"
                    stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"
                  />
                </svg>
                Zero data retention · Processed in memory and immediately discarded
              </div>
            </section>
          )}

          {/* ─── Stripe Elements Payment Form ─── */}
          {phase === 'PAYING' && clientSecret && (
            <section className="ll-pay-section">
              <div className="ll-pay-card">
                <div className="ll-pay-row">
                  <span className="ll-pay-label">Complete your payment</span>
                  <span className="ll-pay-price">
                    {CONFIG.PRICE_DISPLAY} <small>one-time</small>
                  </span>
                </div>
                {CONFIG.STRIPE_PK.includes('mock') || CONFIG.STRIPE_PK.includes('REPLACE_ME') ? (
                  <MockCheckoutForm
                    onPaymentSuccess={handlePaymentSuccess}
                    onPaymentError={handlePaymentError}
                  />
                ) : (
                  stripeElementsOptions && (
                    <Elements stripe={getStripePromise()} options={stripeElementsOptions}>
                      <CheckoutForm
                        onPaymentSuccess={handlePaymentSuccess}
                        onPaymentError={handlePaymentError}
                        isProcessing={isPayProcessing}
                        setIsProcessing={setIsPayProcessing}
                      />
                    </Elements>
                  )
                )}
              </div>
            </section>
          )}

          {/* ─── Processing State ─── */}
          {phase === 'PROCESSING' && (
            <div style={{ textAlign: 'center', padding: '48px 0' }}>
              <span
                className="ll-spinner ll-spinner-light"
                style={{ width: 36, height: 36 }}
              />
              <div
                style={{
                  marginTop: 16,
                  fontSize: 15,
                  fontWeight: 600,
                  color: 'var(--ll-text-primary)',
                }}
              >
                Transforming your data…
              </div>
              <div
                style={{
                  marginTop: 6,
                  fontSize: 13,
                  color: 'var(--ll-text-tertiary)',
                }}
              >
                Payment verified. Running full conversion pipeline.
              </div>
            </div>
          )}

          {/* ─── Success / Download Complete ─── */}
          {phase === 'COMPLETE' && downloadResult && (
            <div className="ll-success">
              <div className="ll-success-icon">✓</div>
              <h2>Transformation Complete</h2>
              <p>
                Your QBO-ready file has been downloaded. Import it directly into
                QuickBooks Online — no further editing required.
              </p>
              <div className="ll-success-meta">
                <span>
                  Profile: <strong>{downloadResult.profile}</strong>
                </span>
                <span>
                  Rows: <strong>{formatNumber(downloadResult.rowCount)}</strong>
                </span>
                {downloadResult.batchCount > 1 && (
                  <span>
                    Files: <strong>{downloadResult.batchCount}</strong>
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
                <button
                  className="ll-pay-btn"
                  style={{ width: 'auto', padding: '12px 28px' }}
                  onClick={() => downloadBlob(downloadResult.blob, downloadResult.filename)}
                >
                  Download Again
                </button>
                <button className="ll-btn-secondary" onClick={resetApp}>
                  Convert Another File
                </button>
              </div>
            </div>
          )}



          {/* ─── FAQ Section ─── */}
          {phase === 'IDLE' && !csvFile && (
            <section className="ll-faq-wrap">
              <h2 className="ll-features-heading">Frequently Asked Questions</h2>
              <div className="ll-faq-grid">
                {/* FAQ Item 1 */}
                <div className={`ll-faq-item ${expandedFaq === 0 ? 'is-expanded' : ''}`}>
                  <button
                    className="ll-faq-trigger"
                    onClick={() => setExpandedFaq(expandedFaq === 0 ? null : 0)}
                    aria-expanded={expandedFaq === 0}
                  >
                    <h3>How does LedgerPass format my Stripe CSV data for QuickBooks Online?</h3>
                    <svg className="ll-faq-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </button>
                  <div className="ll-faq-content">
                    <p>
                      LedgerPass transforms your raw Stripe balance history CSV files into standardized QBO formats by parsing fee columns, separating gross sales, and adjusting dates. The output is structured to align with QuickBooks Online bank feed expectations, eliminating import errors and manual reconciliation overhead. You get clean, import-ready transaction files instantly.
                    </p>
                  </div>
                </div>

                {/* FAQ Item 2 */}
                <div className={`ll-faq-item ${expandedFaq === 1 ? 'is-expanded' : ''}`}>
                  <button
                    className="ll-faq-trigger"
                    onClick={() => setExpandedFaq(expandedFaq === 1 ? null : 1)}
                    aria-expanded={expandedFaq === 1}
                  >
                    <h3>Is my transaction data secure and private?</h3>
                    <svg className="ll-faq-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </button>
                  <div className="ll-faq-content">
                    <p>
                      Absolutely. LedgerPass operates under a strict zero data retention policy. Your files are processed entirely in memory on Cloudflare's stateless edge network and immediately discarded after conversion. We do not store, log, or share any of your transactional data, ensuring your financial information remains fully private and secure.
                    </p>
                  </div>
                </div>

                {/* FAQ Item 3 */}
                <div className={`ll-faq-item ${expandedFaq === 2 ? 'is-expanded' : ''}`}>
                  <button
                    className="ll-faq-trigger"
                    onClick={() => setExpandedFaq(expandedFaq === 2 ? null : 2)}
                    aria-expanded={expandedFaq === 2}
                  >
                    <h3>How does the edge-processing network benefit the performance?</h3>
                    <svg className="ll-faq-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </button>
                  <div className="ll-faq-content">
                    <p>
                      By running on a stateless edge routing network, LedgerPass handles file transformation at nodes closest to you. This reduces latency to milliseconds, making conversions near-instantaneous. Additionally, eliminating central backend databases ensures that your sensitive client bookkeeping data never sits on a vulnerable server, combining speed with industry-standard privacy controls.
                    </p>
                  </div>
                </div>

                {/* FAQ Item 4 */}
                <div className={`ll-faq-item ${expandedFaq === 3 ? 'is-expanded' : ''}`}>
                  <button
                    className="ll-faq-trigger"
                    onClick={() => setExpandedFaq(expandedFaq === 3 ? null : 3)}
                    aria-expanded={expandedFaq === 3}
                  >
                    <h3>What if my converted file doesn't import?</h3>
                    <svg className="ll-faq-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </button>
                  <div className="ll-faq-content">
                    <p>
                      Every purchase is covered by our import-success guarantee. If QuickBooks (or Xero/FreeAgent) rejects a file we produced, email support@getledgerpass.com with the error you saw and we'll either fix the conversion or refund your payment in full — your choice. You can also test the entire flow before paying using the free preview or the built-in sample data.
                    </p>
                  </div>
                </div>
              </div>
            </section>
          )}
        </div>
      </main>

          {/* ─── Footer ─── */}
          <footer className="ll-footer">
            <div className="ll-footer-grid">
              <div className="ll-footer-brand">
                <div className="ll-footer-logo-row">
                  <BrandLogo height={32} />
                </div>
                <p className="ll-footer-desc-text">
                  LedgerPass processes your files entirely in volatile memory via a distributed, stateless edge architecture. Zero data retention. Zero compliance liability.
                </p>
                <p className="ll-footer-copyright">
                  © {new Date().getFullYear()} LedgerPass. All rights reserved.
                </p>
              </div>
              <div className="ll-footer-nav-col">
                <h4 className="ll-footer-nav-title">Trust & Legal</h4>
                <ul className="ll-footer-nav-links">
                  <li>
                    <button className="ll-pay-btn-link" onClick={() => setActiveModal('privacy')}>
                      Privacy Policy
                    </button>
                  </li>
                  <li>
                    <button className="ll-pay-btn-link" onClick={() => setActiveModal('terms')}>
                      Terms of Service
                    </button>
                  </li>
                  <li>
                    <a href="mailto:support@getledgerpass.com?subject=LedgerPass%20Support%20Inquiry">
                      Support & Contact
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </footer>

      {/* Modals */}
      {activeModal && (
        <div 
          className="ll-modal-overlay" 
          onClick={() => setActiveModal(null)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-title"
        >
          <div className="ll-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="ll-modal-header">
              <h2 id="modal-title">
                {activeModal === 'privacy' 
                  ? 'Privacy Policy' 
                  : activeModal === 'terms' 
                  ? 'Terms of Service' 
                  : 'Restore Unlimited Subscription'}
              </h2>
              <button className="ll-modal-close" onClick={() => setActiveModal(null)} aria-label="Close modal">✕</button>
            </div>
            <div className="ll-modal-body">
              {activeModal === 'restore' ? (
                <div className="ll-modal-text">
                  {restoreMode === 'key' ? (
                    <>
                      <p>
                        If you subscribed to the <strong>Unlimited Access Plan</strong>, a license key was automatically generated and saved in your Stripe customer record.
                      </p>
                      <p>
                        To restore your session, please copy and paste the license key from your Stripe receipt email, or use the link provided in the receipt to log in automatically.
                      </p>
                      <form onSubmit={handleRestoreSubmit} style={{ marginTop: '20px' }}>
                        <div className="ll-field-group">
                          <label htmlFor="restore-license-key" className="ll-field-label">License Key</label>
                          <input
                            id="restore-license-key"
                            type="text"
                            placeholder="LP_SUB_..."
                            value={inputKey}
                            onChange={(e) => setInputKey(e.target.value)}
                            className="ll-field-input"
                            disabled={subVerifyLoading}
                            style={{ fontFamily: 'monospace' }}
                          />
                        </div>
                        {modalError && (
                          <div className="ll-error-banner" style={{ marginTop: '12px', padding: '10px 14px', fontSize: '13px' }}>
                            <span className="ll-banner-icon">✕</span>
                            <div>{modalError}</div>
                          </div>
                        )}
                        <button
                          type="submit"
                          className="ll-pay-btn"
                          style={{ marginTop: '20px' }}
                          disabled={subVerifyLoading}
                        >
                          {subVerifyLoading ? (
                            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                              <span className="ll-spinner" />
                              Verifying Key…
                            </span>
                          ) : (
                            'Verify & Restore Subscription'
                          )}
                        </button>
                      </form>
                      <div style={{ marginTop: '20px', textAlign: 'center' }}>
                        <button
                          type="button"
                          className="ll-pay-btn-link"
                          onClick={() => {
                            setRestoreMode('email');
                            setModalError(null);
                          }}
                        >
                          Forgot your key? Email me my key
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <p>
                        Forgot your license key? Enter the email address associated with your subscription, and we will email you your key and magic login link.
                      </p>
                      {recoverSuccess ? (
                        <div 
                          className="ll-detected" 
                          style={{ 
                            justifyContent: 'center', 
                            background: 'rgba(52, 211, 153, 0.08)', 
                            borderColor: 'rgba(52, 211, 153, 0.2)',
                            color: '#34D399',
                            padding: '16px',
                            borderRadius: 'var(--ll-radius-lg)',
                            fontSize: '13.5px',
                            lineHeight: 1.5,
                            marginTop: '20px'
                          }}
                        >
                          <span className="ll-detected-icon">✓</span>
                          <div>
                            If an active subscription exists for that email, a recovery link has been sent. Please check your inbox (and spam folder).
                          </div>
                        </div>
                      ) : (
                        <form onSubmit={handleRecoverSubmit} style={{ marginTop: '20px' }}>
                          <div className="ll-field-group">
                            <label htmlFor="recover-email" className="ll-field-label">Email Address</label>
                            <input
                              id="recover-email"
                              type="email"
                              placeholder="you@example.com"
                              value={recoverEmail}
                              onChange={(e) => setRecoverEmail(e.target.value)}
                              className="ll-field-input"
                              disabled={recoverLoading}
                            />
                          </div>
                          {modalError && (
                            <div className="ll-error-banner" style={{ marginTop: '12px', padding: '10px 14px', fontSize: '13px' }}>
                              <span className="ll-banner-icon">✕</span>
                              <div>{modalError}</div>
                            </div>
                          )}
                          <button
                            type="submit"
                            className="ll-pay-btn"
                            style={{ marginTop: '20px' }}
                            disabled={recoverLoading}
                          >
                            {recoverLoading ? (
                              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                                <span className="ll-spinner" />
                                Sending Email…
                              </span>
                            ) : (
                              'Send Recovery Link'
                            )}
                          </button>
                        </form>
                      )}
                      <div style={{ marginTop: '20px', textAlign: 'center' }}>
                        <button
                          type="button"
                          className="ll-pay-btn-link"
                          onClick={() => {
                            setRestoreMode('key');
                            setModalError(null);
                          }}
                        >
                          Back to enter key
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ) : activeModal === 'privacy' ? (
                <div className="ll-modal-text">
                  <p><strong>Stateless Processing Commitment</strong></p>
                  <p>LedgerPass is engineered with a strict stateless architecture. Your uploaded files are processed entirely in volatile server memory (RAM) and are immediately destroyed upon session completion. We do not store, duplicate, or share user-uploaded financial data with third parties.</p>
                  
                  <p><strong>Lightweight Analytics Disclosure</strong></p>
                  <p>We use privacy-focused, lightweight analytics for monitoring site performance, errors, and conversion rates. We do not track personal identifying information, customer records, or financial transaction histories. No tracking cookies are used to build advertising profiles.</p>
                  
                  <p><strong>Third-Party Infrastructure</strong></p>
                  <p>While we leverage secure third-party hosting and edge networks (such as Cloudflare) to route and transform data packets near your physical location, all user data remains in volatile execution memory. Your private financial data is never written to permanent disk storage, databases, or third-party storage clusters.</p>
                  
                  <p><strong>Payment Processing</strong></p>
                  <p>We collect your email address during payment to deliver receipt confirmation and Stripe payment receipts. Payments are processed securely via Stripe. LedgerPass never sees, stores, or holds your raw card credentials.</p>
                </div>
              ) : (
                <div className="ll-modal-text">
                  <p><strong>Scope Disclaimer & Utility Status</strong></p>
                  <p>LedgerPass is a data-formatting utility designed on-demand. We are not Certified Public Accountants (CPAs), bookkeepers, or financial advisors. The services and output files provided by LedgerPass do not constitute financial, legal, tax, or professional accounting advice.</p>
                  
                  <p><strong>User Verification & Responsibility</strong></p>
                  <p>You are solely and exclusively responsible for verifying the accuracy, integrity, and completeness of the final transformed CSV/QBO data before importing it into QuickBooks or any other accounting software. LedgerPass does not audit or guarantee the numerical accuracy of source or destination payloads.</p>

                  <p><strong>Stateless Architecture & Data Records</strong></p>
                  <p>Because LedgerPass operates on a strictly stateless architecture, we do not store, back up, host, or retain any of your uploaded files or converted data. Once your session or download completes, the volatile memory buffer is purged. You are entirely responsible for maintaining your own backup files and original financial records.</p>
                  
                  <p><strong>Limitation of Liability & "AS IS" Warranty</strong></p>
                  <p>LedgerPass is provided on an "AS IS" and "AS AVAILABLE" basis, disclaiming all warranties of any kind, whether express or implied. Under no circumstances shall LedgerPass, its operators, or affiliates be liable for any direct, indirect, incidental, special, consequential, or exemplary damages, including but not limited to bookkeeping errors, tax penalties, system downtime, or financial inaccuracies resulting from the use or inability to use this utility.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
